import React, { ChangeEvent, FormEvent, useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  ArrowLeft,
  Calendar,
  Check,
  ChevronDown,
  CircleDot,
  Clock,
  FileText,
  Flag,
  Headset,
  Image as ImageIcon,
  Info,
  LayoutGrid,
  MoreVertical,
  Paperclip,
  Play,
  Plus,
  RefreshCw,
  Search,
  Send,
  Smartphone,
  Upload,
  UserRound,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  COUNTRY_CODES,
  HELP_TICKETS,
  SAMPLE_DEVICE,
  attachmentCount,
  cloneTickets,
  filterTickets,
  sortTickets,
  ticketStats,
  type AttachmentKind,
  type QueryFilter,
  type QuerySort,
  type SupportTicket,
  type TicketAttachment,
  type TicketMessage,
  type TicketStatus,
} from '../../data/help-query-mock';
import { QueryArt } from './QueryArt';
import './help-query.css';

const PREVIEW_MS = 680;
const MAX_BYTES = 50 * 1024 * 1024;
const MAX_FILES = 6;

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: 'Open',
  pending: 'Pending',
  closed: 'Closed',
};

const CHIPS: { id: QueryFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'pending', label: 'Pending' },
  { id: 'closed', label: 'Closed' },
];

const STATS: { id: QueryFilter; label: string; key: 'all' | 'awaiting' | 'progress' | 'resolved'; tone: string; icon: LucideIcon }[] = [
  { id: 'all', label: 'All tickets', key: 'all', tone: 'all', icon: FileText },
  { id: 'pending', label: 'Awaiting reply', key: 'awaiting', tone: 'wait', icon: Clock },
  { id: 'open', label: 'In progress', key: 'progress', tone: 'run', icon: RefreshCw },
  { id: 'closed', label: 'Resolved', key: 'resolved', tone: 'done', icon: Check },
];

export interface HelpQueryViewProps {
  fullName: string;
  email: string;
  /** Sample inbox until the support API is wired. Pass `[]` for the empty state. */
  initialTickets?: SupportTicket[];
  /** Leave unset to preview skeletons. Pass a real flag once the support API is wired. */
  loading?: boolean;
}

interface DraftFile {
  id: string;
  name: string;
  sizeLabel: string;
  kind: AttachmentKind;
}

interface NewQueryDraft {
  subject: string;
  description: string;
  whatsapp: string;
  files: DraftFile[];
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function usePreviewLoading(enabled: boolean): boolean {
  const [loading, setLoading] = useState(() => enabled && !prefersReducedMotion());

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    if (prefersReducedMotion()) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const id = window.setTimeout(() => setLoading(false), PREVIEW_MS);
    return () => window.clearTimeout(id);
  }, [enabled]);

  return loading;
}

function useMinWidth(minPx: number): boolean {
  const query = `(min-width: ${minPx}px)`;
  const read = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    try {
      return window.matchMedia(query).matches;
    } catch {
      return true;
    }
  };
  const [matches, setMatches] = useState(read);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    let media: MediaQueryList;
    try {
      media = window.matchMedia(query);
    } catch {
      return undefined;
    }
    const onChange = () => setMatches(media.matches);
    onChange();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    }
    return undefined;
  }, [query]);

  return matches;
}

function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'You';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

function formatBytes(size: number): string {
  if (!Number.isFinite(size) || size < 1024) return `${Math.max(0, Math.round(size))} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatStamp(date: Date): { day: string; exact: string } {
  try {
    const day = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
    const time = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
    return { day, exact: `${day}, ${time}` };
  } catch {
    const iso = date.toISOString();
    return { day: iso.slice(0, 10), exact: iso };
  }
}

function kindFor(file: File): AttachmentKind {
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('image/')) return 'image';
  return 'file';
}

function takeFiles(list: FileList | null, current: DraftFile[]): { files: DraftFile[]; error: string } {
  const incoming = Array.from(list ?? []);
  if (incoming.length === 0) return { files: current, error: '' };
  if (current.length + incoming.length > MAX_FILES) {
    return { files: current, error: `You can attach up to ${MAX_FILES} files.` };
  }
  const next = [...current];
  for (const file of incoming) {
    if (file.size > MAX_BYTES) {
      return { files: current, error: `${file.name} is over 50 MB.` };
    }
    next.push({
      id: `${file.name}-${file.size}-${next.length}`,
      name: file.name,
      sizeLabel: formatBytes(file.size),
      kind: kindFor(file),
    });
  }
  return { files: next, error: '' };
}

function nextCode(tickets: SupportTicket[]): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ0123456789';
  const used = new Set(tickets.map((ticket) => ticket.code));
  let code = 'ML-2026-AAAAA';
  do {
    code = `ML-2026-${Array.from({ length: 5 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('')}`;
  } while (used.has(code));
  return code;
}

function openingText(ticket: SupportTicket): string {
  return ticket.messages.find((entry) => entry.author === 'user')?.body || ticket.preview;
}

function Skel({ className = '' }: { className?: string }) {
  return <span className={`hq-skel ${className}`} />;
}

function HelpQuerySkeleton() {
  return (
    <div className="hq" role="status" aria-busy="true">
      <p className="visually-hidden">Loading help and queries</p>
      <div className="hq-head" data-testid="skeleton-header">
        <Skel className="hq-skel--btn" />
      </div>
      <div className="hq-stats" data-testid="skeleton-stats">
        {STATS.map((stat) => (
          <Skel key={stat.id} className="hq-skel--stat" />
        ))}
      </div>
      <div className="hq-board hq-board--skel" data-pane="both">
        <div className="hq-pane" data-testid="skeleton-list">
          <Skel className="hq-skel--search" />
          <Skel className="hq-skel--row" />
          <Skel className="hq-skel--row" />
          <Skel className="hq-skel--row" />
          <Skel className="hq-skel--row" />
        </div>
        <div className="hq-pane" data-testid="skeleton-thread">
          <Skel className="hq-skel--line" />
          <Skel className="hq-skel--title" />
          <Skel className="hq-skel--bubble" />
          <Skel className="hq-skel--bubble hq-skel--bubble-right" />
          <Skel className="hq-skel--bubble" />
        </div>
        <div className="hq-pane" data-testid="skeleton-info">
          <Skel className="hq-skel--line" />
          <Skel className="hq-skel--row" />
          <Skel className="hq-skel--row" />
          <Skel className="hq-skel--row" />
          <Skel className="hq-skel--card" />
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: TicketStatus }) {
  return <span className={`hq-pill hq-pill--${status}`}>{STATUS_LABEL[status]}</span>;
}

function FileCard({ file, tone }: { file: TicketAttachment; tone: 'user' | 'support' }) {
  const Icon = file.kind === 'image' ? ImageIcon : file.kind === 'video' ? Play : FileText;
  return (
    <div className={`hq-file hq-file--${tone}`}>
      <span className="hq-file__icon" aria-hidden="true">
        <Icon size={16} />
      </span>
      <span>
        <strong>{file.name}</strong>
        <small>{file.sizeLabel}</small>
      </span>
    </div>
  );
}

function QueryPlaceholder({
  title,
  text,
  showClear,
  onClear,
  onNew,
}: {
  title: string;
  text: string;
  showClear: boolean;
  onClear: () => void;
  onNew: () => void;
}) {
  return (
    <section className="hq-placeholder" data-testid="illustration-placeholder" aria-label="No queries">
      <QueryArt label="Support illustration" />
      <h2>{title}</h2>
      <p>{text}</p>
      <div className="hq-placeholder__actions">
        {showClear && (
          <button type="button" className="hq-btn hq-btn--ghost" onClick={onClear}>
            Clear filters
          </button>
        )}
        <button type="button" className="hq-btn hq-btn--primary" onClick={onNew}>
          <Plus size={16} aria-hidden="true" />
          New query
        </button>
      </div>
    </section>
  );
}

function NewQueryDialog({
  fullName,
  email,
  onClose,
  onCreate,
}: {
  fullName: string;
  email: string;
  onClose: () => void;
  onCreate: (draft: NewQueryDraft) => void;
}) {
  const titleId = useId();
  const subjectId = useId();
  const bodyId = useId();
  const phoneId = useId();
  const hintId = useId();
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [dial, setDial] = useState<string>(COUNTRY_CODES[0].dial);
  const [whatsapp, setWhatsapp] = useState('');
  const [files, setFiles] = useState<DraftFile[]>([]);
  const [fileError, setFileError] = useState('');
  const [errors, setErrors] = useState<{ subject?: string; description?: string; whatsapp?: string }>({});

  useEffect(() => {
    subjectRef.current?.focus();
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const onFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const result = takeFiles(event.target.files, files);
    setFiles(result.files);
    setFileError(result.error);
    event.target.value = '';
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const nextErrors: { subject?: string; description?: string; whatsapp?: string } = {};
    const subjectText = subject.trim();
    const bodyText = description.trim();
    const digits = whatsapp.replace(/[^\d]/g, '');
    if (!subjectText) nextErrors.subject = 'Enter a subject.';
    if (!bodyText) nextErrors.description = 'Describe the issue so we can help.';
    if (whatsapp.trim() && !/^\d{7,15}$/.test(digits)) {
      nextErrors.whatsapp = 'Enter a valid WhatsApp number, or leave it blank.';
    }
    setErrors(nextErrors);
    if (nextErrors.subject) {
      subjectRef.current?.focus();
      return;
    }
    if (nextErrors.description) {
      bodyRef.current?.focus();
      return;
    }
    if (nextErrors.whatsapp || fileError) return;
    onCreate({
      subject: subjectText,
      description: bodyText,
      whatsapp: digits ? `${dial} ${digits}` : '',
      files,
    });
  };

  return (
    <div className="hq-modal" data-testid="query-dialog-backdrop" onMouseDown={onClose}>
      <div
        className="hq-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="hq-modal__top">
          <div>
            <h2 id={titleId}>New query</h2>
            <p>Tell us about your issue and we'll get back to you soon.</p>
          </div>
          <button type="button" className="hq-modal__x" aria-label="Close" onClick={onClose}>
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <section className="hq-included" aria-label="Included with your query">
          <div className="hq-included__intro">
            <p className="hq-included__kicker">Included with your query</p>
            <p>Device and account details are sent automatically to help us provide better support.</p>
          </div>
          <div className="hq-included__grid">
            <article>
              <span className="hq-included__icon" aria-hidden="true">
                <Smartphone size={18} />
              </span>
              <div>
                <small>Device</small>
                <strong>{SAMPLE_DEVICE.name}</strong>
                <em>{SAMPLE_DEVICE.detail}</em>
              </div>
            </article>
            <article>
              <span className="hq-included__icon" aria-hidden="true">
                <UserRound size={18} />
              </span>
              <div>
                <small>Account</small>
                <strong>{fullName}</strong>
                <em>{email}</em>
              </div>
            </article>
          </div>
        </section>

        <form className="hq-form" noValidate onSubmit={submit}>
          <div className="hq-field">
            <label htmlFor={subjectId}>
              Subject <span aria-hidden="true">*</span>
            </label>
            <div className="hq-control">
              <input
                id={subjectId}
                ref={subjectRef}
                value={subject}
                maxLength={100}
                required
                placeholder="Briefly describe your issue"
                autoComplete="off"
                onChange={(event) => setSubject(event.target.value.slice(0, 100))}
              />
              <span className={`hq-count${subject.length >= 90 ? ' is-hot' : ''}`}>{subject.length}/100</span>
            </div>
            {errors.subject && <p className="hq-error" role="alert">{errors.subject}</p>}
          </div>

          <div className="hq-field">
            <label htmlFor={phoneId}>
              WhatsApp number <em>(optional)</em>
            </label>
            <div className="hq-phone">
              <select aria-label="Country code" value={dial} onChange={(event) => setDial(event.target.value)}>
                {COUNTRY_CODES.map((country) => (
                  <option key={country.dial} value={country.dial}>
                    {country.flag} {country.dial}
                  </option>
                ))}
              </select>
              <input
                id={phoneId}
                value={whatsapp}
                inputMode="numeric"
                autoComplete="tel"
                placeholder="Enter your WhatsApp number"
                aria-describedby={hintId}
                onChange={(event) => setWhatsapp(event.target.value)}
              />
              <span className="hq-info-dot" aria-hidden="true">
                <Info size={16} />
              </span>
            </div>
            <p className="hq-hint" id={hintId}>We may contact you on WhatsApp if needed.</p>
            {errors.whatsapp && <p className="hq-error" role="alert">{errors.whatsapp}</p>}
          </div>

          <div className="hq-field">
            <label htmlFor={bodyId}>
              Describe your issue <span aria-hidden="true">*</span>
            </label>
            <div className="hq-control hq-control--area">
              <textarea
                id={bodyId}
                ref={bodyRef}
                value={description}
                maxLength={1000}
                required
                rows={4}
                placeholder="Please provide detailed information about your issue, steps to reproduce, and any error messages..."
                onChange={(event) => setDescription(event.target.value.slice(0, 1000))}
              />
              <span className={`hq-count${description.length >= 900 ? ' is-hot' : ''}`}>{description.length}/1000</span>
            </div>
            {errors.description && <p className="hq-error" role="alert">{errors.description}</p>}
          </div>

          <div className="hq-field">
            <div className="hq-drop">
              <div className="hq-drop__copy">
                <Paperclip size={18} aria-hidden="true" />
                <div>
                  <strong>Add attachments (optional)</strong>
                  <span>You can attach images, videos, logs, or documents (max 50 MB per file).</span>
                </div>
              </div>
              <button type="button" className="hq-choose" onClick={() => fileRef.current?.click()}>
                <Upload size={15} aria-hidden="true" />
                Choose files
              </button>
              <input
                ref={fileRef}
                className="visually-hidden"
                type="file"
                multiple
                aria-label="Attach files"
                accept="image/*,video/*,.pdf,.txt,.log,.zip,.json,.csv,.doc,.docx"
                onChange={onFiles}
              />
            </div>
            {files.length > 0 && (
              <div className="hq-files">
                {files.map((file) => (
                  <span key={file.id} className="hq-filechip">
                    <span>{file.name}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${file.name}`}
                      onClick={() => setFiles((current) => current.filter((item) => item.id !== file.id))}
                    >
                      <X size={12} aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {fileError && <p className="hq-error" role="alert">{fileError}</p>}
          </div>

          <div className="hq-modal__foot">
            <button type="button" className="hq-btn hq-btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="hq-btn hq-btn--primary">
              <Send size={16} aria-hidden="true" />
              Send query
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const HelpQueryView: React.FC<HelpQueryViewProps> = ({
  fullName,
  email,
  initialTickets,
  loading,
}) => {
  const person = fullName.trim() || 'Account';
  const mail = email.trim() || 'No email on file';
  const mark = initials(person);
  const searchId = useId();
  const sortId = useId();
  const replyHintId = useId();
  const replyFileRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const split = useMinWidth(860);
  const preview = usePreviewLoading(loading == null);
  const busy = loading ?? preview;
  const [items, setItems] = useState<SupportTicket[]>(() => cloneTickets(initialTickets ?? HELP_TICKETS));
  const [selectedId, setSelectedId] = useState<string | null>(
    () => (initialTickets ?? HELP_TICKETS)[0]?.id ?? null
  );
  const [filter, setFilter] = useState<QueryFilter>('all');
  const [sort, setSort] = useState<QuerySort>('newest');
  const [query, setQuery] = useState('');
  const [pane, setPane] = useState<'list' | 'thread'>('list');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reply, setReply] = useState('');
  const [replyFiles, setReplyFiles] = useState<DraftFile[]>([]);
  const [replyError, setReplyError] = useState('');

  const visible = sortTickets(filterTickets(items, filter, query), sort);
  const active = visible.find((ticket) => ticket.id === selectedId) ?? visible[0] ?? null;
  const activeId = active?.id ?? '';
  const stats = ticketStats(items);

  const closeDialog = useCallback(() => setDialogOpen(false), []);
  const openDialog = () => {
    setMenuOpen(false);
    setDialogOpen(true);
  };

  useEffect(() => {
    setDetailsOpen(false);
    setMenuOpen(false);
    setReply('');
    setReplyFiles([]);
    setReplyError('');
  }, [activeId]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const clearFilters = () => {
    setFilter('all');
    setQuery('');
  };

  const addQuery = (draft: NewQueryDraft) => {
    const now = new Date();
    const stamp = formatStamp(now);
    const code = nextCode(items);
    const attachments = draft.files.map((file) => ({
      id: file.id,
      name: file.name,
      sizeLabel: file.sizeLabel,
      kind: file.kind,
    }));
    const ticket: SupportTicket = {
      id: code,
      code,
      subject: draft.subject,
      preview: draft.description.length > 96 ? `${draft.description.slice(0, 93)}...` : draft.description,
      status: 'pending',
      category: 'App functionality',
      priority: 'Normal',
      createdAt: now.toISOString(),
      createdOnLabel: stamp.day,
      createdExact: stamp.exact,
      listTimeLabel: 'Just now',
      lastResponseLabel: 'Just now',
      deviceName: SAMPLE_DEVICE.name,
      deviceDetail: SAMPLE_DEVICE.detail,
      whatsapp: draft.whatsapp || undefined,
      messages: [
        {
          id: `${code}-open`,
          author: 'system',
          body: `You opened this ticket · ${stamp.exact}`,
          atLabel: '',
        },
        {
          id: `${code}-user`,
          author: 'user',
          body: draft.description,
          atLabel: 'Just now',
          attachments: attachments.length > 0 ? attachments : undefined,
        },
      ],
    };
    setItems((current) => [ticket, ...current]);
    setSelectedId(ticket.id);
    setFilter('all');
    setQuery('');
    setPane('thread');
    setDialogOpen(false);
  };

  const updateStatus = (id: string, status: TicketStatus) => {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, status, lastResponseLabel: 'Just now' } : item))
    );
    setMenuOpen(false);
  };

  const onReplyFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const result = takeFiles(event.target.files, replyFiles);
    setReplyFiles(result.files);
    setReplyError(result.error);
    event.target.value = '';
  };

  const sendReply = (event: FormEvent) => {
    event.preventDefault();
    if (!active) return;
    const text = reply.trim();
    if (!text && replyFiles.length === 0) return;
    const entry: TicketMessage = {
      id: `reply-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      author: 'user',
      body: text,
      atLabel: 'Just now',
      attachments: replyFiles.length
        ? replyFiles.map((file) => ({
            id: file.id,
            name: file.name,
            sizeLabel: file.sizeLabel,
            kind: file.kind,
          }))
        : undefined,
    };
    setItems((current) =>
      current.map((item) =>
        item.id === active.id
          ? { ...item, lastResponseLabel: 'Just now', messages: [...item.messages, entry] }
          : item
      )
    );
    setReply('');
    setReplyFiles([]);
    setReplyError('');
  };

  if (busy) return <HelpQuerySkeleton />;

  const emptyTitle = items.length === 0 ? 'No queries yet' : 'No queries in this view';
  const emptyText = items.length === 0
    ? 'Send a query and the MockLoc team will reply here. Screenshots and screen recordings help us fix the issue faster.'
    : 'Nothing in this list matches your search or filter. Clear it, or send a new query.';

  return (
    <div className="hq">
      <header className="hq-head">
        <button type="button" className="hq-btn hq-btn--primary" onClick={openDialog}>
          <Plus size={16} aria-hidden="true" />
          New query
        </button>
      </header>

      <section className="hq-stats" aria-label="Query summary">
        {STATS.map((stat) => {
          const Icon = stat.icon;
          return (
            <button
              key={stat.id}
              type="button"
              className={`hq-stat${filter === stat.id ? ' is-on' : ''}`}
              aria-pressed={filter === stat.id}
              onClick={() => setFilter((current) => (stat.id !== 'all' && current === stat.id ? 'all' : stat.id))}
            >
              <span className={`hq-stat__icon hq-stat__icon--${stat.tone}`} aria-hidden="true">
                <Icon size={18} />
              </span>
              <span>
                <small>{stat.label}</small>
                <strong>{stats[stat.key]}</strong>
              </span>
            </button>
          );
        })}
      </section>

      {active == null ? (
        <QueryPlaceholder
          title={emptyTitle}
          text={emptyText}
          showClear={items.length > 0}
          onClear={clearFilters}
          onNew={openDialog}
        />
      ) : (
        <div className="hq-board" data-pane={split ? 'both' : pane}>
          <section className="hq-pane hq-list" aria-label="Query list">
            <div className="hq-list__tools">
              <div className="hq-search">
                <Search size={16} aria-hidden="true" />
                <label className="visually-hidden" htmlFor={searchId}>
                  Search tickets
                </label>
                <input
                  id={searchId}
                  type="search"
                  value={query}
                  placeholder="Search tickets..."
                  autoComplete="off"
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
              <div className="hq-filters" role="group" aria-label="Filter queries">
                {CHIPS.map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    className={`hq-chip${filter === chip.id ? ' is-on' : ''}`}
                    aria-pressed={filter === chip.id}
                    onClick={() => setFilter(chip.id)}
                  >
                    {chip.label}
                  </button>
                ))}
                <label className="visually-hidden" htmlFor={sortId}>
                  Sort queries
                </label>
                <select
                  id={sortId}
                  className="hq-sort"
                  value={sort}
                  onChange={(event) => setSort(event.target.value as QuerySort)}
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                </select>
              </div>
            </div>
            <ul className="hq-list__items" aria-label="Queries">
              {visible.map((ticket) => {
                const count = attachmentCount(ticket);
                const selected = ticket.id === active.id;
                return (
                  <li key={ticket.id}>
                    <button
                      type="button"
                      className={`hq-ticket${selected ? ' is-active' : ''}`}
                      aria-current={selected ? 'true' : undefined}
                      onClick={() => {
                        setSelectedId(ticket.id);
                        setPane('thread');
                        setMenuOpen(false);
                      }}
                    >
                      <span className="hq-ticket__top">
                        <span className="hq-ticket__code">{ticket.code}</span>
                        <span className="hq-ticket__time">{ticket.listTimeLabel}</span>
                      </span>
                      <strong>{ticket.subject}</strong>
                      <span className="hq-ticket__preview">
                        <p>{ticket.preview}</p>
                        <StatusPill status={ticket.status} />
                      </span>
                      {count > 0 && (
                        <span className="hq-ticket__files">
                          <Paperclip size={12} aria-hidden="true" />
                          {count} attachment{count === 1 ? '' : 's'}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="hq-pane hq-thread" aria-label="Conversation">
            {!split && (
              <button type="button" className="hq-back" onClick={() => setPane('list')}>
                <ArrowLeft size={16} aria-hidden="true" />
                All queries
              </button>
            )}
            <div className="hq-thread__head">
              <div className="hq-thread__top">
                <div className="hq-thread__meta">
                  <span className="hq-ticket__code">{active.code}</span>
                  <StatusPill status={active.status} />
                </div>
                <div className="hq-menu-anchor" ref={menuRef}>
                  <button
                    type="button"
                    className="hq-icon-btn"
                    aria-label="Ticket actions"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen((open) => !open)}
                  >
                    <MoreVertical size={18} aria-hidden="true" />
                  </button>
                  {menuOpen && (
                    <div className="hq-menu" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => updateStatus(active.id, active.status === 'closed' ? 'open' : 'closed')}
                      >
                        {active.status === 'closed' ? 'Reopen query' : 'Mark resolved'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <h2>{active.subject}</h2>
              <p className="hq-thread__created">Created on {active.createdOnLabel}</p>
            </div>

            <div className="hq-details">
              <button
                type="button"
                className="hq-details__btn"
                aria-expanded={detailsOpen}
                onClick={() => setDetailsOpen((open) => !open)}
              >
                Ticket details
                <ChevronDown size={16} className={detailsOpen ? 'is-open' : ''} aria-hidden="true" />
              </button>
              {detailsOpen && (
                <dl className="hq-details__body">
                  <div>
                    <dt>Category</dt>
                    <dd>{active.category}</dd>
                  </div>
                  <div>
                    <dt>Priority</dt>
                    <dd>{active.priority}</dd>
                  </div>
                  <div>
                    <dt>Device</dt>
                    <dd>{active.deviceName}</dd>
                  </div>
                  <div>
                    <dt>App</dt>
                    <dd>{active.deviceDetail}</dd>
                  </div>
                  {active.whatsapp && (
                    <div>
                      <dt>WhatsApp</dt>
                      <dd>{active.whatsapp}</dd>
                    </div>
                  )}
                  <div>
                    <dt>Description</dt>
                    <dd>{openingText(active)}</dd>
                  </div>
                </dl>
              )}
            </div>

            <div className="hq-stream">
              <span className="hq-day">{active.createdOnLabel}</span>
              {active.messages.map((entry) => {
                if (entry.author === 'system') {
                  return (
                    <p key={entry.id} className="hq-system">
                      <UserRound size={14} aria-hidden="true" />
                      <span>{entry.body}</span>
                    </p>
                  );
                }
                const mine = entry.author === 'user';
                return (
                  <article key={entry.id} className={`hq-msg hq-msg--${entry.author}`}>
                    <span className={`hq-avatar hq-avatar--${entry.author}`} aria-hidden="true">
                      {mine ? mark : <Headset size={15} />}
                    </span>
                    <div className="hq-msg__body">
                      <div className="hq-bubble">
                        {entry.body ? <p>{entry.body}</p> : null}
                        {entry.attachments?.map((file) => (
                          <FileCard key={file.id} file={file} tone={mine ? 'user' : 'support'} />
                        ))}
                      </div>
                      {entry.atLabel ? <time>{entry.atLabel}</time> : null}
                    </div>
                  </article>
                );
              })}
            </div>

            <form className="hq-compose" onSubmit={sendReply}>
              {replyFiles.length > 0 && (
                <div className="hq-files">
                  {replyFiles.map((file) => (
                    <span key={file.id} className="hq-filechip">
                      <span>{file.name}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${file.name}`}
                        onClick={() => setReplyFiles((current) => current.filter((item) => item.id !== file.id))}
                      >
                        <X size={12} aria-hidden="true" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="hq-compose__row">
                <button
                  type="button"
                  className="hq-icon-btn"
                  aria-label="Add attachment"
                  onClick={() => replyFileRef.current?.click()}
                >
                  <Paperclip size={18} aria-hidden="true" />
                </button>
                <input
                  ref={replyFileRef}
                  className="visually-hidden"
                  type="file"
                  multiple
                  aria-label="Attach files to reply"
                  accept="image/*,video/*,.pdf,.txt,.log,.zip,.json,.csv,.doc,.docx"
                  onChange={onReplyFiles}
                />
                <textarea
                  aria-label="Write a reply"
                  aria-describedby={replyHintId}
                  placeholder="Write a reply..."
                  rows={2}
                  value={reply}
                  onChange={(event) => setReply(event.target.value)}
                />
                <button
                  type="submit"
                  className="hq-send"
                  aria-label="Send reply"
                  disabled={!reply.trim() && replyFiles.length === 0}
                >
                  <Send size={16} aria-hidden="true" />
                </button>
              </div>
              {replyError && <p className="hq-error" role="alert">{replyError}</p>}
              <p className="hq-hint" id={replyHintId}>
                You can attach images, videos, logs, or documents (max 50 MB).
              </p>
            </form>
          </section>

          <section className="hq-pane hq-info" aria-label="Ticket information">
            <h2>Ticket information</h2>
            {(
              [
                { icon: LayoutGrid, label: 'Category', value: active.category },
                { icon: Flag, label: 'Priority', value: active.priority },
                { icon: CircleDot, label: 'Status', value: STATUS_LABEL[active.status] },
                { icon: Calendar, label: 'Created', value: active.createdExact },
                { icon: Clock, label: 'Last response', value: active.lastResponseLabel },
                {
                  icon: Paperclip,
                  label: 'Attachments',
                  value: `${attachmentCount(active)} file${attachmentCount(active) === 1 ? '' : 's'}`,
                },
              ] as const
            ).map((row) => {
              const Icon = row.icon;
              return (
                <div className="hq-info__row" key={row.label}>
                  <Icon size={16} aria-hidden="true" />
                  <div>
                    <small>{row.label}</small>
                    {row.label === 'Status' ? <StatusPill status={active.status} /> : <strong>{row.value}</strong>}
                  </div>
                </div>
              );
            })}
            <div className="hq-help">
              <QueryArt label="Support illustration" />
              <div>
                <h3>
                  <span className="hq-dot" aria-hidden="true" />
                  We're here to help
                </h3>
                <p>Our support team is available and usually replies within 24 hours.</p>
              </div>
            </div>
          </section>
        </div>
      )}

      {dialogOpen && (
        <NewQueryDialog fullName={person} email={mail} onClose={closeDialog} onCreate={addQuery} />
      )}
    </div>
  );
};

export default HelpQueryView;
